import * as THREE from 'three';
import { createRenderer } from '../core/renderer/createRenderer';
import { createScene } from '../core/scene/createScene';
import { Input } from '../core/input/Input';
import { Clock } from './time/Clock';
import { HUD } from './debug/HUD';
import { World } from '../game/world/World';
import { detectQuality } from '../core/quality/QualitySettings';
import { PostFX } from '../core/postfx/PostFX';

export type GameStatus = {
  fps: number;
  debugText: string;
  uiState: ReturnType<World['getUiState']>;
};

export class Game {
  private readonly quality = detectQuality();

  private readonly renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private readonly clock = new Clock();
  private readonly input = new Input();
  private readonly hud: HUD;
  private readonly world: World;
  private readonly camera: THREE.PerspectiveCamera;

  private postfx: PostFX | null = null;

  private raf = 0;
  private running = false;
  private paused = false;
  private lastFpsSampleT = 0;
  private frames = 0;
  private fps = 0;
  private showDebugHud = false;

  constructor(
    private readonly mount: HTMLElement,
    private readonly onStatus?: (status: GameStatus) => void,
    options?: { showDebugHud?: boolean }
  ) {
    this.showDebugHud = options?.showDebugHud ?? false;
    this.renderer = createRenderer(mount, this.quality);
    this.scene = createScene();

    const { width, height } = mount.getBoundingClientRect();
    this.camera = new THREE.PerspectiveCamera(70, width / Math.max(1, height), 0.1, 4000);

    this.hud = new HUD(mount);
    this.world = new World(this.scene, this.camera, this.input, { quality: this.quality });

    if (this.quality.postfx.enabled) {
      this.postfx = new PostFX(this.renderer, this.scene, this.camera, this.quality);
      // Underground-Neon Feeling
      this.postfx.setBloom(0.85, 0.25, 0.88);
    }

    window.addEventListener('resize', this.onResize, { passive: true });
    this.onResize();
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.clock.reset();
    this.loop();
  }

  setPaused(next: boolean) {
    this.paused = next;
  }

  reset() {
    this.paused = false;
    this.clock.reset();
    this.world.resetCar();
  }

  startRace() {
    this.world.startRace();
  }

  setOpenWorldMode(next: boolean) {
    this.world.setOpenWorldMode(next);
  }

  setCarById(carId: string) {
    this.world.setCarById(carId);
  }

  setUrbanTrack(trackId: string) {
    this.world.setUrbanTrack(trackId);
  }

  setCarTuning(carId: string, tuning: Record<string, number | string | boolean>) {
    this.world.setCarTuning(carId, tuning as any);
  }

  setGhostEnabled(next: boolean) {
    this.world.setGhostEnabled(next);
  }

  setVirtualKey(code: string, down: boolean) {
    this.input.setVirtualKey(code, down);
  }
  
  setVirtualJoystick(x: number, y: number, active: boolean) {
    this.input.setVirtualJoystick(x, y, active);
  }

  setDebugHudVisible(visible: boolean) {
    this.showDebugHud = visible;
  }

  getUiState() {
    return this.world.getUiState();
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    this.input.dispose();
    this.hud.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  private loop = () => {
    this.raf = requestAnimationFrame(this.loop);

    const dt = this.clock.tick();
    this.input.update();

    if (!this.paused) this.world.update(dt);

    if (this.postfx) this.postfx.render(dt);
    else this.renderer.render(this.scene, this.camera);

    this.updateFps(dt);

    const status: GameStatus = {
      fps: this.fps,
      debugText: this.world.getDebugText(this.fps),
      uiState: this.world.getUiState()
    };

    this.hud.setVisible(this.showDebugHud);
    if (this.showDebugHud) {
      this.hud.setText(status.debugText);
    }
    this.onStatus?.(status);
  };

  private updateFps(dt: number) {
    this.frames++;
    this.lastFpsSampleT += dt;
    if (this.lastFpsSampleT >= 0.25) {
      this.fps = Math.round(this.frames / this.lastFpsSampleT);
      this.frames = 0;
      this.lastFpsSampleT = 0;
    }
  }

  private onResize = () => {
    const { width, height } = this.mount.getBoundingClientRect();
    this.camera.aspect = Math.max(1e-6, width / Math.max(1, height));
    this.camera.updateProjectionMatrix();

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.quality.pixelRatioCap));
    this.renderer.setSize(width, height, false);
    this.postfx?.setSize(width, height);
  };
}
